import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaEye, FaUsers, FaChartLine, FaTrophy } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import PageHeader from '../components/PageHeader';
import analyticsService from '../services/analyticsService';
import { useAuth } from '../context/AuthContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function OverviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [mostViewedProducts, setMostViewedProducts] = useState([]);
  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };

  const auth = useAuth();

  useEffect(() => {
    // Wait until auth context has loaded user info before fetching
    if (!auth.loading) {
      fetchAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch overview data (30 days) - admin or seller-scoped
      const overviewData = auth.isAdmin ? await analyticsService.getDashboardOverview(30) : await analyticsService.getDashboardOverviewForVendor(30);
      setOverview(overviewData);

      // Fetch trending products (last 7 days)
      const trendingData = auth.isAdmin ? await analyticsService.getTrendingProducts(7, 5) : await analyticsService.getTrendingProductsForVendor(7, 5);
      setTrendingProducts(trendingData.trending_products || []);

      // Fetch most viewed products (all time)
      const mostViewedData = auth.isAdmin ? await analyticsService.getMostViewedProducts(5) : await analyticsService.getMostViewedProductsForVendor(5);
      setMostViewedProducts(mostViewedData.most_viewed_products || []);

    } catch (err) {
      console.error('Failed to load analytics:', err);
      if (auth.isAdmin) {
        setError('Failed to load analytics data. Please ensure you are logged in as an admin.');
      } else {
        setError('Failed to load analytics for your companies.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = overview?.views_by_day ? {
    labels: overview.views_by_day.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Product Views',
        data: overview.views_by_day.map(item => item.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  if (loading) {
    return (
      <Container fluid className="px-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Overview"
        subtitle="Monitor key metrics and product performance"
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {overview && (
        <>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Total Views (30d)</p>
                      <h3 className="mb-0">{overview.total_views?.toLocaleString()}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <FaEye className="text-primary" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Unique Visitors</p>
                      <h3 className="mb-0">{overview.unique_visitors?.toLocaleString()}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <FaUsers className="text-success" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Anonymous Views</p>
                      <h3 className="mb-0">{overview.anonymous_views?.toLocaleString()}</h3>
                    </div>
                    <div className="bg-warning bg-opacity-10 p-3 rounded">
                      <FaChartLine className="text-warning" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Top Product Views</p>
                      <h3 className="mb-0">{overview.most_viewed_product?.view_count || 0}</h3>
                      <small className="text-muted">
                        {overview.most_viewed_product?.product_title?.substring(0, 20)}
                        {overview.most_viewed_product?.product_title?.length > 20 ? '...' : ''}
                      </small>
                    </div>
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      <FaTrophy className="text-danger" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Views Chart */}
          {chartData && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h5 className="mb-3">Product Views (Last 30 Days)</h5>
                    <div style={{ height: '300px' }}>
                      <Line data={chartData} options={chartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Trending and Most Viewed Products */}
          <Row className="g-3 mb-4">
            {/* Trending Products (Last 7 Days) */}
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="mb-3">Trending Products (7 Days)</h5>
                  {trendingProducts.length > 0 ? (
                    <Table hover className="mb-0">
                      <tbody>
                        {trendingProducts.map((product, index) => (
                          <tr 
                            key={product.product_id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/products/edit/${product.product_id}`)}
                          >
                            <td className="align-middle" width="40">
                              <Badge bg="primary" pill>{index + 1}</Badge>
                            </td>
                            <td className="align-middle" width="60">
                              {product.image ? (
                                <img
                                  src={getImageUrl(product.image)}
                                  alt={product.product_title}
                                  className="rounded"
                                  width="48"
                                  height="48"
                                  style={{ objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="rounded bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold"
                                  style={{ width: '48px', height: '48px', fontSize: '20px' }}
                                >
                                  {product.product_title?.charAt(0)}
                                </div>
                              )}
                            </td>
                            <td className="align-middle">
                              <div className="fw-semibold">{product.product_title}</div>
                              <small className="text-muted">{product.category}</small>
                            </td>
                            <td className="align-middle text-end">
                              <div className="fw-semibold text-primary">{product.view_count}</div>
                              <small className="text-muted">views</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>No trending products yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Most Viewed Products (All Time) */}
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h5 className="mb-3">Most Viewed Products (All Time)</h5>
                  {mostViewedProducts.length > 0 ? (
                    <Table hover className="mb-0">
                      <tbody>
                        {mostViewedProducts.map((product, index) => (
                          <tr 
                            key={product.product_id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/products/edit/${product.product_id}`)}
                          >
                            <td className="align-middle" width="40">
                              <Badge bg="success" pill>{index + 1}</Badge>
                            </td>
                            <td className="align-middle" width="60">
                              {product.image ? (
                                <img
                                  src={getImageUrl(product.image)}
                                  alt={product.product_title}
                                  className="rounded"
                                  width="48"
                                  height="48"
                                  style={{ objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="rounded bg-success bg-opacity-10 d-flex align-items-center justify-content-center text-success fw-bold"
                                  style={{ width: '48px', height: '48px', fontSize: '20px' }}
                                >
                                  {product.product_title?.charAt(0)}
                                </div>
                              )}
                            </td>
                            <td className="align-middle">
                              <div className="fw-semibold">{product.product_title}</div>
                              <small className="text-muted">
                                {product.unique_viewers} unique viewers
                              </small>
                            </td>
                            <td className="align-middle text-end">
                              <div className="fw-semibold text-success">{product.view_count}</div>
                              <small className="text-muted">views</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>No product views yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default OverviewPage;
